// src/pages/DriversPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    CircularProgress,
    Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { createDriver, deactivateDriver, listDrivers, updateDriver, reactivateDriver, deleteDriverHard, resendDriverInvite, type Driver } from "../api/driversApi";
import { formatPhoneInput } from "../utils/phone";

type StatusFilter = "ACTIVE" | "INACTIVE" | "SUSPENDED" | "ALL";

function statusChip(status: Driver["status"]) {
    switch (status) {
        case "ACTIVE":
            return <Chip label="ACTIVE" color="success" size="small" variant="outlined" />;
        case "INACTIVE":
            return <Chip label="INACTIVE" color="warning" size="small" variant="outlined" />;
        case "SUSPENDED":
            return <Chip label="SUSPENDED" color="error" size="small" variant="outlined" />;
        default:
            return <Chip label={status} size="small" variant="outlined" />;
    }
}

function normalizePhoneInput(v: string) {
    return v.replace(/\D/g, "");
}

export default function DriversPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [snack, setSnack] = useState<string | null>(null);

    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [status, setStatus] = useState<StatusFilter>("ALL");
    const [q, setQ] = useState("");

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState<Driver | null>(null);

    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        vehicle_type: "",
        status: "ACTIVE" as Driver["status"],
    });

    const fetchDrivers = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await listDrivers({ status, q: q.trim() || undefined });
            setDrivers(res.drivers || []);
        } catch (e: any) {
            setError(e?.message || "Failed to load drivers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return drivers;
        return drivers.filter((d) => {
            const hay = `${d.name} ${d.phone} ${d.email || ""}`.toLowerCase();
            return hay.includes(term);
        });
    }, [drivers, q]);

    const openCreate = () => {
        setForm({ name: "", phone: "", email: "", vehicle_type: "", status: "ACTIVE" });
        setCreateOpen(true);
    };

    const openEdit = (d: Driver) => {
        setSelected(d);
        setForm({
            name: d.name || "",
            phone: d.phone || "",
            email: d.email || "",
            vehicle_type: d.vehicle_type || "",
            status: d.status,
        });
        setEditOpen(true);
    };

    const submitCreate = async () => {
        setError("");
        try {
            await createDriver({
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(), // REQUIRED
                phone: form.phone.trim() ? normalizePhoneInput(form.phone) : null,
                vehicle_type: form.vehicle_type.trim() || null,
                status: form.status,
            });

            setCreateOpen(false);
            setSnack("Driver created");
            fetchDrivers();
        } catch (e: any) {
            setError(e?.message || "Failed to create driver");
        }
    };

    const submitEdit = async () => {
        if (!selected) return;
        setError("");
        try {
            await updateDriver(selected.id, {
                name: form.name.trim(),
                phone: normalizePhoneInput(form.phone),
                email: form.email.trim() ? form.email.trim().toLowerCase() : null,
                vehicle_type: form.vehicle_type.trim() || null,
                status: form.status,
            });
            setEditOpen(false);
            setSelected(null);
            setSnack("Driver updated");
            fetchDrivers();
        } catch (e: any) {
            setError(e?.message || "Failed to update driver");
        }
    };

    const doDeactivate = async (d: Driver) => {
        setError("");
        try {
            await deactivateDriver(d.id);
            setSnack("Driver deactivated");
            fetchDrivers();
        } catch (e: any) {
            setError(e?.message || "Failed to deactivate driver");
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
                <Typography variant="h4" fontWeight={800}>
                    Drivers
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value as StatusFilter)}
                        >
                            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                            <MenuItem value="ALL">ALL</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        placeholder="Search name / phone / email"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") fetchDrivers();
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: "100%", sm: 320 } }}
                    />

                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                        Add Driver
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2} sx={{ mt: 3 }}>
                    {filtered.map((d) => (
                        <Card key={d.id} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                            <Typography fontWeight={700}>{d.name}</Typography>
                                            {statusChip(d.status)}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            Phone: {d.phone} {d.email ? `• Email: ${d.email}` : ""} {d.vehicle_type ? `• Vehicle: ${d.vehicle_type}` : ""}
                                        </Typography>
                                    </Box>

                                   <Stack direction="row" spacing={1} alignItems="center">
  <IconButton onClick={() => openEdit(d)} aria-label="edit">
    <EditIcon />
  </IconButton>

  {d.status === "ACTIVE" && !d.has_password && (
    <Button
      size="small"
      onClick={async () => {
        await resendDriverInvite(d.id);
        setSnack("Invite email resent");
      }}
    >
      Resend Invite
    </Button>
  )}

  {d.status === "ACTIVE" ? (
    <IconButton onClick={() => doDeactivate(d)} aria-label="deactivate">
      <DeleteIcon />
    </IconButton>
  ) : (
    <>
      <Button
        size="small"
        onClick={async () => {
          await reactivateDriver(d.id);
          setSnack("Driver reactivated");
          fetchDrivers();
        }}
      >
        Reactivate
      </Button>

      <Button
        size="small"
        color="error"
        onClick={async () => {
          if (!window.confirm("Delete driver permanently? This cannot be undone.")) return;
          await deleteDriverHard(d.id);
          setSnack("Driver deleted");
          fetchDrivers();
        }}
      >
        Delete
      </Button>
    </>
  )}
</Stack>



                                </Stack>
                            </CardContent>
                        </Card>
                    ))}

                    {!filtered.length && (
                        <Typography color="text.secondary" sx={{ mt: 2 }}>
                            No drivers found.
                        </Typography>
                    )}
                </Stack>
            )}

            {/* Create dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add Driver</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                        <TextField
                            label="Phone"
                            type="tel"
                            inputProps={{ inputMode: "tel" }}
                            value={form.phone}
                            onChange={(e) => setForm((p) => ({ ...p, phone: formatPhoneInput(e.target.value) }))}
                            helperText="Digits only is fine (we normalize)."
                        />
                        <TextField
                            label="Email"
                            required
                            value={form.email}
                            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                            error={!form.email.trim()}
                            helperText={!form.email.trim() ? "Email is required for driver login" : ""}
                        />
                        <TextField label="Vehicle Type (optional)" value={form.vehicle_type} onChange={(e) => setForm((p) => ({ ...p, vehicle_type: e.target.value }))} />

                        <FormControl>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={form.status}
                                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as any }))}
                            >
                                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                                <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submitCreate}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Driver</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                        <TextField label="Phone" type="tel" inputProps={{ inputMode: "tel" }} value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: formatPhoneInput(e.target.value) }))} />
                        <TextField label="Email (optional)" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                        <TextField label="Vehicle Type (optional)" value={form.vehicle_type} onChange={(e) => setForm((p) => ({ ...p, vehicle_type: e.target.value }))} />

                        <FormControl>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={form.status}
                                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as any }))}
                            >
                                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                                <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submitEdit}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={2500} onClose={() => setSnack(null)} message={snack} />
        </Box>
    );
}
